import type { CellarNote } from "@corklog/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { fetchCellarNotes, markCellarNotesRead } from "../api.js";
import { useCellar } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";
import { formatScore } from "../score.js";

/**
 * Every tasting note in the cellar, newest evening first.
 *
 * This is the screen for `10.10`, and it exists because of a hole that only
 * showed up once a cellar was actually shared. A guest may write tasting notes
 * and nothing else — that is the one deliberate hole in *read-only*, and it is
 * there because the bottles most worth having a note on are the ones drunk
 * with somebody else. But a note has only ever been legible on the page of the
 * wine it was written on, so the owner of a hundred and twenty-three wines had
 * no way of learning that anybody had written one. The only mark a guest can
 * leave was the one mark nobody could find.
 *
 * **Both halves of the request are answered here, deliberately.** The
 * indicator says how many are new; this page shows the new ones *and the old
 * ones*, because a page that showed only what arrived since Tuesday would make
 * the cellar's memory of its own evenings reachable for exactly one visit. The
 * new ones are marked instead. It is the same answer *Luckor* gives: the way
 * in stays, and what has been dealt with is marked rather than removed.
 *
 * **What is new first, then the evenings.** `tastedOn` is what a note is about
 * and is the order a record of dinners is read in, so it decides within each
 * group; `writtenAt` is what *new* is measured against, because a note typed
 * up this morning about a dinner last spring is news this morning. Ordering
 * the whole list by the evening was tried and driven, and it did exactly what
 * that sentence predicts: the March note the indicator had just promised sat
 * below everything drunk since, off the bottom of the screen. So the two
 * groups are separated and labelled, and the second date is printed on a new
 * note alone — where it explains the mark rather than competing with it.
 */
type State =
  | { status: "loading" }
  | { status: "ready"; notes: CellarNote[]; unread: number }
  | { status: "failed" };

export function Notes() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const scale = useCellar()?.tastingScale ?? null;
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(() => {
    void fetchCellarNotes(cellarId)
      .then((body) =>
        setState({ status: "ready", notes: body.notes, unread: body.unread }),
      )
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  /**
   * The bookmark moves once the list has been drawn, and once per visit.
   *
   * After rather than during, because what is marked on the screen is what was
   * new when it opened: a reader who scrolls past three new notes should still
   * see them marked while they are reading them, and find them unmarked the
   * next time they come. That is the same promise the walk through the
   * cabinets makes about a row it has just filled in.
   *
   * The ref is what stops `useReload` — which reloads when the tab comes back
   * — from sending this again on every return. Failure is silent on purpose:
   * nothing the reader asked for has gone wrong, and the worst outcome is that
   * the notes are still marked new next time, which is the harmless direction
   * to fail in.
   */
  const marked = useRef(false);
  useEffect(() => {
    if (state.status !== "ready" || marked.current) return;
    marked.current = true;
    void markCellarNotesRead(cellarId).catch(() => {});
  }, [cellarId, state.status]);

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="notes" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("notes.intro")}</p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("notes.loadFailed")}
        </p>
      )}

      {state.status === "ready" &&
        (state.notes.length === 0 ? (
          <p className="text-muted mt-8 max-w-prose text-sm">{t("notes.empty")}</p>
        ) : (
          <>
            <p className="text-muted mt-6 text-sm">
              {state.unread > 0
                ? t("notes.areNew", { count: state.unread })
                : t("notes.allSeen", { count: state.notes.length })}
            </p>

            {/* Two lists when there is news, one when there is not.

                The server puts the new ones first — ordering the whole thing
                by the evening was driven and buried them: a note written up
                now about a dinner in March lands below everything drunk since,
                with a mark nobody scrolls far enough to see. The headings are
                what make the jump from March back to August read as an order
                rather than as a fault.

                Two lists rather than a heading dropped inside one, because a
                heading is not an item: a list whose third entry is the word
                *Tidigare* says there are three notes when there are two, to a
                screen reader and to anybody counting. */}
            {state.unread > 0 && (
              <>
                <h2 className="font-display text-ink mt-8 text-base">
                  {t("notes.sinceLast")}
                </h2>
                <NoteList
                  cellarId={cellarId}
                  notes={state.notes.filter((note) => note.isNew)}
                  scale={scale}
                />
                {state.notes.some((note) => !note.isNew) && (
                  <h2 className="font-display text-ink mt-8 text-base">
                    {t("notes.earlier")}
                  </h2>
                )}
              </>
            )}
            {state.notes.some((note) => !note.isNew) && (
              <NoteList
                cellarId={cellarId}
                notes={state.notes.filter((note) => !note.isNew)}
                scale={scale}
              />
            )}
          </>
        ))}
    </section>
  );
}

/** One run of notes under one heading. Its own component so that the two
 *  groups cannot drift into two different-looking lists. */
function NoteList({
  cellarId,
  notes,
  scale,
}: {
  cellarId: string;
  notes: readonly CellarNote[];
  scale: { max: number; hasDecimals: boolean } | null;
}) {
  return (
    <ul className="border-line mt-3 border-t">
      {notes.map((note) => (
        <NoteRow
          key={note.id}
          cellarId={cellarId}
          note={note}
          scaleMax={scale?.max ?? null}
          scaleHasDecimals={scale?.hasDecimals ?? false}
        />
      ))}
    </ul>
  );
}

/** The day, as this application writes days: the Swedish long form, because a
 *  record of evenings reads as evenings and not as `2026-03-14`. */
function writtenDay(iso: string): string {
  return new Date(iso).toLocaleDateString(locale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * One note: what was drunk, what was said, and by whom.
 *
 * The wine leads the row, in the number-and-name shape the wine list's own row
 * leads with, and it is the link — a note is almost always read with *what was
 * that again* attached to it, and the answer is the wine's page.
 */
function NoteRow({
  cellarId,
  note,
  scaleMax,
  scaleHasDecimals,
}: {
  cellarId: string;
  note: CellarNote;
  scaleMax: number | null;
  scaleHasDecimals: boolean;
}) {
  const { t } = useTranslation();

  // The year, or the word for a wine that has none. The same two states the
  // wine list prints, so a wine reads the same in both places.
  const year = note.isNonVintage
    ? t("wines.nvShort")
    : note.vintage === null
      ? ""
      : String(note.vintage);

  return (
    <li className="border-line border-b py-4">
      <div className="grid grid-cols-[2.75rem_1fr] gap-x-4">
        <div className="font-display text-muted pt-0.5 text-right text-lg">
          {note.wineNumber}
        </div>

        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-4">
            {/* `h3`, under the group's own `h2` — the same two levels the walk
                through the cabinets uses for a shelf and the wines on it. */}
            <h3 className="font-display truncate text-lg leading-tight">
              <Link
                to={`/kallare/${cellarId}/viner/${note.wineId}`}
                className="underline-offset-4 hover:underline"
              >
                {note.wineName}
              </Link>
            </h3>
            {/* A word rather than a dot, for the reason the walk through the
                cabinets prints *Ifyllt* rather than a tick: a mark carrying
                state says nothing about what the state is. */}
            {note.isNew && (
              <span className="text-accent shrink-0 text-sm">{t("notes.new")}</span>
            )}
          </div>
          <p className="text-muted mt-0.5 truncate text-sm">
            {note.producer}
            {year !== "" && <>, {year}</>}
          </p>

          {/* The note itself, which is the point of the row and is therefore
              the one thing here set at reading size. */}
          {note.text !== "" && <p className="mt-2 max-w-prose">{note.text}</p>}

          <p className="text-muted mt-2 text-sm">
            {/* Who, and when it was drunk.

                **Your own notes say *Du*, and everybody else's say what they
                are to this cellar.** A name is not an answer to *who wrote
                this*: on a cellar shared with three people it is three names
                the reader has to know by heart to tell their own record from
                their guests'. Naming the reader rather than printing their own
                name is what makes the distinction land without reading — the
                eye finds *Du* or it finds a stranger, and the role behind the
                stranger says on what footing they were here.

                The author is null for a note whose writer's account has gone —
                anonymised rather than deleted, so that satisfying somebody's
                erasure does not take the cellar's record of its own evening
                with it. */}
            {note.mine ? (
              t("notes.byYou")
            ) : (
              <>
                {note.authorName ?? t("notes.authorGone")}
                {note.authorRole !== null && note.authorRole !== undefined && (
                  <>, {t(`role.${note.authorRole}`).toLowerCase()}</>
                )}
              </>
            )}{" "}
            · {writtenDay(note.tastedOn)}
            {/* `10.25`: a wine drunk somewhere else says where, beside the
                day, because the two together are the evening. */}
            {note.place !== null && <> · {note.place}</>}
            {note.score !== null && scaleMax !== null && (
              <>
                {" · "}
                <span className="font-display text-ink">
                  {formatScore(note.score, scaleHasDecimals)}
                </span>
                /{scaleMax}
              </>
            )}
            {note.isFaulty && <> · {t("notes.faulty")}</>}
          </p>

          {/* Only on a new note, and only where the two dates disagree.

              The line exists for one job: to explain why something is marked
              *Ny* above a dinner from March. On a note that is not new nobody
              is asking, and this cellar shows why that matters — the import
              wrote all fourteen of its notes on one day in September about
              evenings in July and August, so a rule of *wherever they differ*
              printed a second date on almost every row and made the page two
              dates arguing about which one it was about. */}
          {note.isNew && note.writtenAt.slice(0, 10) !== note.tastedOn && (
            <p className="text-muted mt-1 text-sm">
              {t("notes.writtenOn", { day: writtenDay(note.writtenAt) })}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}
